import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { OnDestroy$ } from '@pdtec/ngx-observable-lifecycle';
import { environment } from '../../../environments/environment';
import { HotelToursFiltersComponent } from '../../components/hotel-tours/filters/filters.component';
import { HotelToursComponent } from '../../components/hotel-tours/hotel-tours.component';
import {
    HotelPriceCalendarComponent
} from '../../components/hotel-tours/short-search-params/calendar/calendar.component';
import {
    ShortSearchParamsComponent
} from '../../components/hotel-tours/short-search-params/short-search-params.component';
import {
    SearchProgressLinearComponent
} from '../../components/search-progress-linear/search-progress-linear.component';
import { HOTEL_TOURS_PAGE_CONFIG, HotelToursPageConfig } from '../../config/hotel-tours-list-page.config';
import { HotelToursSearchService } from '../../services/search/hotel-tours-search.service';
import { WindowRefService } from '../../services/window-ref.service';
import { filter, takeUntil } from 'rxjs/operators';
import { EventBusService } from '../../services/event-bus.service';
import { BrandList } from '../../models';

@Component({
    selector: 'page-hotel-tours-list',
    templateUrl: environment.mobile ? './hotel-tours-list.component.mobile.html' : './hotel-tours-list.component.html',
    styleUrls: environment.mobile ? ['./hotel-tours-list.component.mobile.scss'] : ['./hotel-tours-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        HotelToursComponent,
        ShortSearchParamsComponent,
        SearchProgressLinearComponent,
        HotelPriceCalendarComponent,
        HotelToursFiltersComponent
    ]
})
export class PageHotelToursListComponent extends OnDestroy$ implements OnInit {
    public showStartHint = true;
    get isTicketsBrand(): boolean {
        return this.hotelToursPageConfig.brandId === BrandList.TICKETS_KZ;
    }

    public showPriceCalendar = false;

    constructor(
        private hotelToursSearchService: HotelToursSearchService,
        private windowService: WindowRefService,
        private eventBusService: EventBusService,
        private cdRef: ChangeDetectorRef,
        @Inject(HOTEL_TOURS_PAGE_CONFIG) private hotelToursPageConfig: HotelToursPageConfig,
    ) {
        super();
    }

    ngOnInit() {
        this.hotelToursSearchService.trackPageOpen(this.hotelToursPageConfig.searchFormValues);
        this.windowService.nativeWindow.lastSearchParams = this.hotelToursPageConfig.searchFormValues;
        this.hotelToursSearchService.setLastSearchParams(this.hotelToursPageConfig.searchFormValues);
        const initialSearchParams = this.hotelToursSearchService.getLastSearchParams();
        const shouldAutoStartSearch = this.shouldAutoStartSearch(initialSearchParams);
        this.showStartHint = this.isDesktopHotelsSearch
            ? !shouldAutoStartSearch
            : true;

        this.hotelToursSearchService.newSearchStarted$
            .pipe(filter(searchId => !!searchId), takeUntil(this.ngOnDestroy$))
            .subscribe(() => {

                this.showStartHint = false;
                if (!environment.mobile && this.isShowPriceCalendar) {
                    this.showPriceCalendar = true;
                }
                this.cdRef.detectChanges();
            });

        if (!environment.mobile) {
            this.eventBusService.openCalendarFromEmptyResult$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
                if (this.isShowPriceCalendar) {
                    this.showPriceCalendar = true;
                    this.cdRef.detectChanges();
                }
            });
        }

        if (shouldAutoStartSearch) {
            this.tryAutoStartSearch(initialSearchParams);
        }
    }

    private tryAutoStartSearch(initialSearchParams: any) {
        this.hotelToursSearchService.startNewSearchByHotelId(initialSearchParams);
    }

    private shouldAutoStartSearch(values: any): boolean {
        if (!this.hotelToursPageConfig.autoStartSearch) {
            return false;
        }
        if (!environment.mobile) {
            return true;
        }

        return !this.isDefaultSearchFormValues(values);
    }

    get isDesktopHotelsSearch(): boolean {
        return !environment.mobile && !!this.hotelToursPageConfig.searchFormValues?.hotelsSearch;
    }

    get isShowPriceCalendar(): boolean {
        return !this.hotelToursPageConfig.searchFormValues?.hotelsSearch;
    }

    private isDefaultSearchFormValues(values: any): boolean {
        if (environment.mobile || !values) {
            return false;
        }

        const dateFrom = this.normalizeDate(values.dateFrom);
        const dateTo = this.normalizeDate(values.dateTo);

        if (!dateFrom || !dateTo) {
            return false;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const defaultDateFrom = this.addDays(today, 8);
        const defaultDateTo = this.addDays(today, 12);

        const isDefaultDateFrom = this.isSameDate(dateFrom, defaultDateFrom);
        const isDefaultDateTo = this.isSameDate(dateTo, defaultDateTo);
        const isDefaultNights = Number(values.nightsFrom) === 6 && Number(values.nightsTo) === 8;
        const isDefaultAdults = Number(values.adults) === 2;
        const isDefaultChildren = Array.isArray(values.childAges) && values.childAges.length === 0;
        const isDefaultForceDates = Number(values.forceDates || 0) === 0;

        return isDefaultDateFrom
            && isDefaultDateTo
            && isDefaultNights
            && isDefaultAdults
            && isDefaultChildren
            && isDefaultForceDates;
    }

    private normalizeDate(value: string | Date): Date | null {
        if (!value) {
            return null;
        }

        if (value instanceof Date) {
            const nextDate = new Date(value);
            nextDate.setHours(0, 0, 0, 0);
            return Number.isNaN(nextDate.getTime()) ? null : nextDate;
        }

        const normalizedValue = String(value).trim();
        const dottedDateMatch = normalizedValue.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
        const date = dottedDateMatch
            ? new Date(Number(dottedDateMatch[3]), Number(dottedDateMatch[2]) - 1, Number(dottedDateMatch[1]))
            : new Date(normalizedValue);

        if (Number.isNaN(date.getTime())) {
            return null;
        }

        date.setHours(0, 0, 0, 0);
        return date;
    }

    private isSameDate(first: Date, second: Date): boolean {
        return first.getTime() === second.getTime();
    }

    private addDays(date: Date, days: number): Date {
        const next = new Date(date);
        next.setDate(next.getDate() + days);
        return next;
    }

    private formatDate(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    }

    public get hotelName() {
        return this.hotelToursPageConfig.hotel.hotelName;
    }
}
